import {Form as InertiaForm, Link} from '@inertiajs/react'
import { type ComponentProps, useEffect, useState } from 'react'

import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Textarea } from '@/components/ui/textarea.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'

import { UserType } from './types.ts'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Save} from "lucide-react";

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  user: UserType
  submitText: string
  canAssignSuperAdmin?: boolean
  canCreateUser?: boolean
}

const roleOptions = [
  { value: 'agent', label: 'Agent' },
  { value: 'admin', label: 'Administrateur' },
  { value: 'super_admin', label: 'Super administrateur' },
  { value: 'manager', label: 'Manager' },
] as const

const statusOptions = [
  { value: 'active', label: 'Actif' },
  { value: 'inactive', label: 'Inactif' },
] as const

export default function Form({
  user,
  submitText,
  canAssignSuperAdmin = false,
  canCreateUser = true,
  ...formProps
}: FormProps) {
  const initialRole = user.role || 'agent'
  const initialStatus = user.status || 'active'
  const [role, setRole] = useState(initialRole)
  const [status, setStatus] = useState(initialStatus)
  const availableRoleOptions = canAssignSuperAdmin
      ? roleOptions
      : roleOptions.filter((option) => option.value !== 'super_admin')

  useEffect(() => {
    setRole(initialRole)
    setStatus(initialStatus)
  }, [initialRole, initialStatus])

  useEffect(() => {
    const canUseCurrentRole = availableRoleOptions.some((option) => option.value === role)
    if (!canUseCurrentRole) {
      setRole('agent')
    }
  }, [availableRoleOptions, role])

  return (
      <InertiaForm
          transform={(data) => ({ user: data })}
          className="contents"
          {...formProps}
      >
        {({ errors, processing }) => (
            <>
              {!canCreateUser && (
                <Card className="md:col-span-2 border-amber-200 bg-amber-50">
                  <CardHeader>
                    <CardTitle>Accès restreint</CardTitle>
                    <CardDescription>
                      Vous n'avez pas l'autorisation de créer des utilisateurs. Veuillez contacter un administrateur.
                    </CardDescription>
                  </CardHeader>
                </Card>
              )}

              <fieldset
                disabled={!canCreateUser}
                className={`grid gap-6 md:grid-cols-2 ${!canCreateUser ? "pointer-events-none opacity-60" : ""}`}
              >
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle> Informations de base</CardTitle>
                    <CardDescription>Entrez les informations d'identification de l'utilisateur</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-4">
                        <Label htmlFor="first_name">Prénoms</Label>
                        <Input
                            type="text"
                            name="first_name"
                            id="first_name"
                            placeholder="Prénoms"
                            defaultValue={user.first_name}
                        />
                        {errors.first_name && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.first_name.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <Label htmlFor="last_name">Nom</Label>
                        <Input
                            type="text"
                            name="last_name"
                            id="last_name"
                            placeholder="Nom"
                            defaultValue={user.last_name}
                        />
                        {errors.last_name && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.last_name.join(', ')}
                            </div>
                        )}
                      </div>
                      <div className="space-y-4">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            type="email"
                            name="email"
                            id="email"
                            placeholder="Email"
                            defaultValue={user.email}
                        />
                        {errors.email && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.email.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <Label htmlFor="contact">Téléphone</Label>
                        <Input
                            type="text"
                            name="contact"
                            id="contact"
                            placeholder="Téléphone"
                            defaultValue={user.contact}
                        />
                        {errors.contact && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.contact.join(', ')}
                            </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Rôle et accès</CardTitle>
                    <CardDescription>Assigner les accès à l'utiliateur</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-4">
                        <Label htmlFor="role">Rôle</Label>
                        <input type="hidden" name="role" value={role} />
                        <Select value={role} onValueChange={setRole}>
                          <SelectTrigger id="role" className={"w-full"}>
                            <SelectValue placeholder="Rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableRoleOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.role && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.role.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <Label htmlFor="status">Statut</Label>
                        <input type="hidden" name="status" value={status} />
                        <Select value={status} onValueChange={setStatus}>
                          <SelectTrigger id="status" className={"w-full"}>
                            <SelectValue placeholder="Statut" />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.status && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.status.join(', ')}
                            </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Securité</CardTitle>
                    <CardDescription>Définissez un mot de passe que l'utilisateur pourra utiliser pour se connecter</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">


                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-4">
                        <Label htmlFor="password">Mot de passe</Label>
                        <Input
                            type="password"
                            name="password"
                            id="password"
                            placeholder="Mot de passe"
                        />
                        {errors.password && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.password.join(', ')}
                            </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <Label htmlFor="password_confirmation">Confirmation de mot de passe</Label>
                        <Input
                            type="password"
                            name="password_confirmation"
                            id="password_confirmation"
                            placeholder="Confirmation du mot de passe"
                        />
                        {errors.password_confirmation && (
                            <div className="text-red-500 px-3 py-2 font-medium">
                              {errors.password_confirmation.join(', ')}
                            </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle>Notes additionneles</CardTitle>
                    <CardDescription>Ajouter une note ou des informations sur l'utilisateur</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="note">Note</Label>
                      <Textarea
                          name="note"
                          id="note"
                          placeholder="Notes"
                          defaultValue={user.note}
                      />
                      {errors.note && (
                          <div className="text-red-500 px-3 py-2 font-medium">
                            {errors.note.join(', ')}
                          </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div/>
                <div className="mt-6 flex justify-end gap-4">
                  <Link href="/admin/users">
                    <Button type="button" variant="outline">
                      Annuler
                    </Button>
                  </Link>
                  <Button disabled={processing || !canCreateUser} type="submit">
                    <Save className="mr-2 h-4 w-4" />
                    {submitText}
                  </Button>
                </div>
              </fieldset>
            </>
        )}
      </InertiaForm>
  )
}
